// Svelte 스토어 가져오기 (상태 관리용)
import { editStore, imageStore, sessionStore, settingsStore } from '../stores';
import { get } from 'svelte/store';
import { API_BASE_URL } from '../utils/config';
import { normalizeTaskResult } from './taskResults';
import { persistentOriginalForEdit } from './taskImageUtils';
import { getAuthHeaders as authenticatedHeaders, parseJSONResponse as parseResponse } from './request';
import { request } from './request';

// URL 생성 헬퍼 함수 - 다른 파일에서도 사용할 수 있게 export
export function constructUrl(path) {
  if (!path) return '';
  // 이미 전체 URL이거나 data/blob URL인 경우 그대로 반환
  if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) {
    return path;
  }
  
  const base = API_BASE_URL || '';
  // path가 /로 시작하면 base 뒤에 바로 붙임, 아니면 /를 추가
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  const fullUrl = `${base}${normalizedPath}`;
  
  // 프로토콜(http://) 부분의 //는 유지하고 그 외의 //는 /로 치환
  return fullUrl.replace(/([^:])\/\//g, '$1/');
}

// 공통 응답 처리 함수
async function parseJSONResponse(response) {
  return parseResponse(response);
}

export async function getAuthHeaders(baseHeaders = {}) {
  return authenticatedHeaders(baseHeaders);
}

/**
 * Celery 비동기 작업 완료 대기 (202 응답 폴링)
 * /api/process_status/{sessionId}?task_id={taskId} 를 주기적으로 조회하여 완료 시까지 대기
 * @param {string} sessionId - 사용자 세션 ID
 * @param {string|null} taskId - 작업 ID (process_id)
 * @param {object} options - intervalMs, timeoutMs 설정
 * @returns {{ status: 'completed', result_url: string }} 완료된 작업 결과
 */
export async function waitForAsyncTask(sessionId, taskId, { intervalMs = 3000, timeoutMs = 300000 } = {}) {
  if (!sessionId || !taskId) throw new Error('작업을 추적할 ID가 없습니다. 갤러리에서 확인해 주세요.');
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, intervalMs));
    const headers = await getAuthHeaders();
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/api/process_status/${encodeURIComponent(sessionId)}?task_id=${encodeURIComponent(taskId)}`, { headers });
    } catch { continue; }
    if ([401, 403].includes(response.status)) await parseJSONResponse(response);
    if (!response.ok) continue;
    const data = await response.json();
    if (data.status === 'completed') return normalizeTaskResult(data);
    if (['failed', 'error'].includes(data.status)) throw new Error(data.error || data.message || '작업 처리에 실패했습니다. 갤러리에서 상태를 확인해 주세요.');
  }
  const error = new Error('아직 작업이 진행 중입니다. 갤러리에서 결과를 확인해 주세요.');
  error.pendingTask = true;
  throw error;
}

// 세션 데이터
function appendSessionData(formData, featureId, options = {}) {
  const session = get(sessionStore);
  if (session.loggedIn && !session.isGuest) {
    const persistentOriginal = persistentOriginalForEdit(get(imageStore));
    const persistedOptions = persistentOriginal
      ? {
          ...options,
          original_url: persistentOriginal,
          upload_url: persistentOriginal,
        }
      : options;
    formData.append('user_id', session.userId);
    formData.append('email', session.email);
    formData.append('session_id', session.sessionId);
    formData.append('function_name', featureId);
    formData.append('function_option', JSON.stringify(persistedOptions));
  }
}

export async function uploadImage(file, _retry = true) {
  console.log('업로드 함수 1');
  const session = get(sessionStore);
  console.log('업로드 함수 2');

  // Android WebView는 File 객체가 외부 저장소를 가리키면 ERR_UPLOAD_FILE_CHANGED가
  // 발생할 수 있다. 파일을 메모리에 읽고 새 File을 만들어 안정적인 업로드를 보장한다.
  let uploadFile = file;
  console.log('업로드 함수 3');
  if (file && file.arrayBuffer) {
    console.log('업로드 함수 3-1');
    try {
      console.log('업로드 함수 3-2');
      
      const buf = await file.arrayBuffer();
      console.log('업로드 함수 3-3');
      uploadFile = new File([buf], file.name, { type: file.type });
      console.log('업로드 함수 3-4');
    } catch (_) {
      console.log('업로드 함수 3-1-1');
      // 오류 시 원본 파일로 폴백
      uploadFile = file;
      console.log('업로드 함수 3-1-2');
    }
  }
  console.log('현재 origin:', window.location.origin);
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('navigator.onLine:', navigator.onLine);

  const formData = new FormData();
  formData.append('image', uploadFile);

  // FormData 항목 출력
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      console.log(`폼 데이터 ${key}: 이름=${value.name}, 타입=${value.type}, 크기=${value.size}`);
    } else {
      console.log(`폼 데이터 ${key}: ${value}`);
    }
  }

  try {
    let headers = {};
    try {
      headers = await getAuthHeaders();
    } catch (_) {}
    console.log('업로드 API 호출 시작')
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      headers,
      body: formData
    });
    console.log('업로드 응답 상태:', response.status, response.statusText);
    // 응답 헤더 확인
    console.log('응답 헤더:', [...response.headers.entries()]);
    console.log('response 객체 파싱 시작');
    const data = await parseJSONResponse(response);
    console.log('response 객체 파싱 완료', data);

    const fullUrl = constructUrl(data.upload_url);

    console.log('서버 응답 URL:', data.upload_url);
    console.log('변환된 전체 URL:', fullUrl);

    // 이미지 스토어 업데이트는 UploadSection.svelte에서 직접 처리
    // 여기서는 URL만 반환
    return fullUrl;
  } catch (error) {
    // 오류 이름/메시지/스택까지 출력
    console.error('이미지 업로드 실패:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack
    });
    if (_retry) {
      // 간헐적인 업로드 실패에 대비해 한번 더 시도
      console.log('한 번 더 업로드 시도');
      await new Promise((r) => setTimeout(r, 500));
      return uploadImage(file, false);
    }
    throw error;
  }
}

// 이미지 업로드, 웹에서 호출할 때
export async function uploadImage_web(file, _retry = true) {
  console.log('업로드 함수 1');
  const session = get(sessionStore);
  console.log('업로드 함수 2');

  // Android WebView는 File 객체가 외부 저장소를 가리키면 ERR_UPLOAD_FILE_CHANGED가
  // 발생할 수 있다. 파일을 메모리에 읽고 새 File을 만들어 안정적인 업로드를 보장한다.
  let uploadFile = file;
  console.log('업로드 함수 3');
  if (file && file.arrayBuffer) {
    console.log('업로드 함수 3-1');
    try {
      console.log('업로드 함수 3-2');
      
      const buf = await file.arrayBuffer();
      console.log('업로드 함수 3-3');
      uploadFile = new File([buf], file.name, { type: file.type });
      console.log('업로드 함수 3-4');
    } catch (_) {
      console.log('업로드 함수 3-1-1');
      // 오류 시 원본 파일로 폴백
      uploadFile = file;
      console.log('업로드 함수 3-1-2');
    }
  }
  console.log('현재 origin:', window.location.origin);
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('navigator.onLine:', navigator.onLine);

  const formData = new FormData();
  formData.append('image', uploadFile);

  // FormData 항목 출력
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      console.log(`폼 데이터 ${key}: 이름=${value.name}, 타입=${value.type}, 크기=${value.size}`);
    } else {
      console.log(`폼 데이터 ${key}: ${value}`);
    }
  }

  try {
    let headers = {};
    try {
      headers = await getAuthHeaders();
    } catch (_) {}
    console.log('업로드 API 호출 시작')
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      headers,
      body: formData
    });
    console.log('업로드 응답 상태:', response.status, response.statusText);
    // 응답 헤더 확인
    console.log('응답 헤더:', [...response.headers.entries()]);
    console.log('response 객체 파싱 시작');
    const data = await parseJSONResponse(response);
    console.log('response 객체 파싱 완료', data);

    const fullUrl = constructUrl(data.upload_url);

    console.log('서버 응답 URL:', data.upload_url);
    console.log('변환된 전체 URL:', fullUrl);

    // 이미지 스토어 업데이트는 UploadSection.svelte에서 직접 처리
    // 여기서는 URL만 반환
    return fullUrl;
  } catch (error) {
    // 오류 이름/메시지/스택까지 출력
    console.error('이미지 업로드 실패:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack
    });
    if (_retry) {
      // 간헐적인 업로드 실패에 대비해 한번 더 시도
      console.log('한 번 더 업로드 시도');
      await new Promise((r) => setTimeout(r, 500));
      return uploadImage_web(file, false);
    }
    throw error;
  }
}

// API 호출 테스트 로그
export async function callRemoveBackgroundMultipleTimes(file, outputFormat = 'png', count = 1) {
  for (let i = 0; i < count; i++) {
    try {
      console.log(`🔁 ${i + 1}번째 API 호출 시작`);
      const result = await removeBackground(file, outputFormat);
      console.log(`✅ ${i + 1}번째 호출 성공`, result);
    } catch (error) {
      console.error(`❌ ${i + 1}번째 호출 실패`, error);
    }
  }
}

// 배경 제거
export async function removeBackground(file, outputFormat = 'png', options = {}) {
  return runQueuedImageGeneration({ endpoint: '/api/remove_background', featureId: 'remove_background', file,
    fields: { output_format: outputFormat, ...options }, loadingText: '배경을 제거하는 중입니다...', timeoutMs: 240000 });
}

// 배경 교체
export async function replaceBackground(file, chooseBackground, outputFormat = 'png', options = {}) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '배경을 교체하는 중입니다...'
  }));
  const session = get(sessionStore);

  const formData = new FormData();
  formData.append('image_file', file);
  formData.append('choose_background', chooseBackground);
  formData.append('output_format', outputFormat);
  appendSessionData(formData, 'replace_background', options);

  try {
    const response = await fetch(`${API_BASE_URL}/api/replace_background`, {
      method: 'POST',
      body: formData
    });
    const data = await parseJSONResponse(response);
    const url = constructUrl(data.download_url);
    imageStore.update(store => ({
      ...store,
      resultImage: url
    }));
    return data;
  } catch (error) {
    console.error('배경 교체 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// 객체 변경
export async function searchReplace(file, prompt, outputFormat = 'png', options = {}) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '객체를 변경하는 중입니다...'
  }));
  const session = get(sessionStore);

  const formData = new FormData();
  formData.append('image_file', file);
  formData.append('prompt', prompt);
  formData.append('output_format', outputFormat);
  appendSessionData(formData, 'search_replace', options);

  try {
    const response = await fetch(`${API_BASE_URL}/api/search_replace`, {
      method: 'POST',
      body: formData
    });
    const data = await parseJSONResponse(response);
    const url = constructUrl(data.download_url);
    imageStore.update(store => ({
      ...store,
      resultImage: url
    }));
    return data;
  } catch (error) {
    console.error('객체 변경 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// 색상 변경
export async function searchRecolor(file, chooseColor, outputFormat = 'png', options = {}) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '색상을 변경하는 중입니다...'
  }));
  const session = get(sessionStore);

  const formData = new FormData();
  formData.append('image_file', file);
  formData.append('choose_color', chooseColor);
  formData.append('output_format', outputFormat);
  appendSessionData(formData, 'search_recolor', options);

  try {
    const response = await fetch(`${API_BASE_URL}/api/search_recolor`, {
      method: 'POST',
      body: formData
    });
    const data = await parseJSONResponse(response);
    const url = constructUrl(data.download_url);
    imageStore.update(store => ({
      ...store,
      resultImage: url
    }));
    return data;
  } catch (error) {
    console.error('색상 변경 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// 이미지 확장
export async function outpaintImage(file, left, right, up, down, creativity, prompt, outputFormat = 'png', options = {}) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '이미지를 확장하는 중입니다...'
  }));
  const session = get(sessionStore);

  const formData = new FormData();
  formData.append('image_file', file);
  formData.append('left', left);
  formData.append('right', right);
  formData.append('up', up);
  formData.append('down', down);
  formData.append('creativity', creativity);
  formData.append('prompt', prompt);
  formData.append('output_format', outputFormat);
  appendSessionData(formData, 'outpaint', options);

  try {
    const response = await fetch(`${API_BASE_URL}/api/outpaint`, {
      method: 'POST',
      body: formData
    });
    const data = await parseJSONResponse(response);
    const url = constructUrl(data.download_url);
    imageStore.update(store => ({
      ...store,
      resultImage: url
    }));
    return data;
  } catch (error) {
    console.error('이미지 확장 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// 스타일 변환
export async function transformStyle(file, selectedStyle, controlStrength, preserveFace, outputFormat = 'png', options = {}) {
  return runQueuedImageGeneration({
    endpoint: '/api/style_transform', featureId: 'style_transform', file,
    fields: { style: selectedStyle, control_strength: controlStrength, preserve_face: preserveFace,
      output_format: outputFormat, language_code: get(settingsStore).language || 'ko', ...options },
    loadingText: '스타일을 변환하는 중입니다...', timeoutMs: 300000
  });
}

// 최신 모바일 편집기에서 사용하는 비동기 생성 API 공통 처리기입니다.
// 서버가 202를 반환하는 기능도 웹에서 결과 화면까지 자연스럽게 이어집니다.
async function runQueuedImageGeneration({ endpoint, featureId, file, fields = {}, loadingText, timeoutMs = 120000 }) {
  const activeTaskId = get(editStore).currentTaskId;
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText
  }));

  const session = get(sessionStore);
  const formData = new FormData();
  formData.append('image_file', file);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value instanceof Blob ? value : String(value));
    }
  });
  appendSessionData(formData, featureId, fields);

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData
    });

    let data;
    if (response.status === 202) {
      const queued = await response.json();
      const sessionId = queued.session_id || session.sessionId;
      if (get(sessionStore).userId === session.userId) editStore.update(store => ({ ...store, dashboardTasks: store.dashboardTasks.map(task => task.id === activeTaskId
        ? { ...task, process_id: queued.process_id || queued.task_id, sessionId, status: 'queued' } : task) }));
      editStore.update(store => ({
        ...store,
        loadingText: queued.message || '백그라운드에서 생성하고 있습니다...'
      }));
      const result = await waitForAsyncTask(sessionId, queued.task_id, { timeoutMs });
      data = {
        ...result,
        process_id: queued.task_id,
        download_url: result.result_url,
        result_image_path: result.result_url,
        result_urls: result.result_urls || null
      };
    } else {
      data = await parseJSONResponse(response);
    }

    data = normalizeTaskResult(data);
    const resultUrl = data.download_url;
    if (resultUrl && get(editStore).currentTaskId === activeTaskId && get(sessionStore).userId === session.userId) {
      imageStore.update(store => ({
        ...store,
        resultImage: constructUrl(resultUrl),
        resultFeature: featureId,
        resultOptions: { ...fields, secondary_image: undefined },
        resultImages: data.result_urls ? data.result_urls.map(url => constructUrl(url)) : null
      }));
    }
    return data;
  } catch (error) {
    console.error(`${featureId} 생성 중 오류:`, error);
    throw error;
  } finally {
    editStore.update(store => ({ ...store, isLoading: false }));
  }
}

export async function transformAdvancedStyle(file, style, outputFormat = 'png', options = {}) {
  return runQueuedImageGeneration({
    endpoint: '/api/advanced_style_transform',
    featureId: 'advanced_style_transform',
    file,
    fields: {
      style,
      output_format: outputFormat,
      language_code: get(settingsStore).language || 'ko',
      ...options
    },
    loadingText: '고급 스타일 이미지를 생성하는 중입니다...',
    timeoutMs: 240000
  });
}

export async function createMbtiImage(file, mbtiType, outputFormat = 'png', options = {}) {
  return runQueuedImageGeneration({
    endpoint: '/api/find_mbti',
    featureId: 'find_mbti',
    file,
    fields: { mbti_type: mbtiType, output_format: outputFormat, ...options },
    loadingText: '반려견 MBTI 이미지를 생성하는 중입니다...',
    timeoutMs: 180000
  });
}

export async function createVideo(file, videoStyle, options = {}) {
  return runQueuedImageGeneration({
    endpoint: '/api/make_video',
    featureId: 'make_video',
    file,
    fields: { video_style: videoStyle, model: 'veo-3.1-lite-generate-preview', ...options },
    loadingText: 'AI 동영상을 생성하는 중입니다. 최대 10분 정도 걸릴 수 있습니다...',
    timeoutMs: 720000
  });
}

// 인페인팅
export async function inpaintImage(file, maskImage, prompt, negativePrompt, seed, outputFormat = 'webp', options = {}) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '인페인팅 중입니다...'
  }));
  const session = get(sessionStore);

  const formData = new FormData();
  formData.append('image_file', file);
  formData.append('mask_image', maskImage); // base64 문자열로 전송
  formData.append('prompt', prompt);
  formData.append('negative_prompt', negativePrompt);
  formData.append('seed', seed);
  formData.append('output_format', outputFormat);
  appendSessionData(formData, 'inpaint', options);

  try {
    const response = await fetch(`${API_BASE_URL}/api/inpaint`, {
      method: 'POST',
      body: formData
    });
    const data = await parseJSONResponse(response);
    const url = constructUrl(data.download_url);
    imageStore.update(store => ({
      ...store,
      resultImage: url
    }));
    return data;
  } catch (error) {
    console.error('인페인팅 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// 해상도 증가
export async function increaseResolution(file, outputFormat = 'png', options = {}) {
  return runQueuedImageGeneration({ endpoint: '/api/resolution_increase', featureId: 'resolution_increase', file,
    fields: { output_format: outputFormat, ...options }, loadingText: '해상도를 높이는 중입니다...', timeoutMs: 240000 });
}

// 객체 지우기
export async function eraseObject(file, maskImage, outputFormat = 'png', seed = 0, options = {}) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '객체를 지우는 중입니다...'
  }));
  const session = get(sessionStore);

  const formData = new FormData();
  formData.append('image_file', file);
  formData.append('mask_image', maskImage); // base64 문자열로 전송
  formData.append('output_format', outputFormat);
  formData.append('seed', seed);
  appendSessionData(formData, 'erase_object', options);

  try {
    const response = await fetch(`${API_BASE_URL}/api/erase_object`, {
      method: 'POST',
      body: formData
    });
    const data = await parseJSONResponse(response);
    const url = constructUrl(data.download_url);
    imageStore.update(store => ({
      ...store,
      resultImage: url
    }));
    return data;
  } catch (error) {
    console.error('객체 지우기 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

// 객체 제거 (Gemini 기반)
// 객체 감지 (Gemini 기반)
export async function detectObjects(file) {
  editStore.update(store => ({
    ...store,
    isLoading: true,
    loadingText: '객체를 감지하는 중입니다...'
  }));

  const formData = new FormData();
  formData.append('image_file', file);

  try {
    const response = await fetch(`${API_BASE_URL}/api/detect_objects_gpt`, {
      method: 'POST',
      body: formData
    });
    return await parseJSONResponse(response);
  } catch (error) {
    console.error('객체 감지 중 오류:', error);
    throw error;
  } finally {
    editStore.update(store => ({
      ...store,
      isLoading: false
    }));
  }
}

export async function removeObject(file, removeObjects, options = {}) {
  return runQueuedImageGeneration({ endpoint: '/api/object_remove', featureId: 'object_remove', file,
    fields: { remove_objects: removeObjects, ...options }, loadingText: '객체를 제거하는 중입니다...', timeoutMs: 240000 });
}

// 이미지 합성 (Gemini 기반)
export async function compositeImage(file, secondaryFile, options = {}) {
  return runQueuedImageGeneration({ endpoint: '/api/image_composite', featureId: 'image_composite', file,
    fields: { secondary_image: secondaryFile, ...options }, loadingText: '이미지를 합성하는 중입니다...', timeoutMs: 300000 });
}

// 대시보드 작업 저장
export async function saveTasksToServer(userId, sessionId, tasks) {
  const session = get(sessionStore);
  if (!session.loggedIn) {
    return;
  }
  try {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    if (get(sessionStore).userId !== userId) return;
    const response = await fetch(`${API_BASE_URL}/api/save_tasks`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userId, session_id: sessionId, tasks })
    });
    return await parseJSONResponse(response);
  } catch (e) {
    console.error('failed to save tasks', e);
    throw e;
  }
}

// 대시보드 작업 불러오기
export async function loadTasksFromServer(userId, sessionId) {
  const session = get(sessionStore);
  if (!session.loggedIn) {
    return [];
  }
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/load_tasks/${userId}/${sessionId}`, { headers });
    return await parseJSONResponse(res);
  } catch (e) {
    console.error('failed to load tasks', e);
    return [];
  }
}

// RION (코인) 잔액 조회
export async function getRionBalance(userId) {
  if (!userId) return null;
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/coins/balance?user_id=${userId}`, { headers });
    return await parseJSONResponse(response);
  } catch (e) {
    console.error('RION 잔액 조회 실패', e);
    return null;
  }
}

// 작업 히스토리 (전체) 조회
export async function getWorkHistoryPage(userId, { cursor, limit = 30 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor != null) params.set('cursor', String(cursor));
  const data = await request(`/api/work_history/${encodeURIComponent(userId)}?${params}`);
  return Array.isArray(data) ? { items: data, has_more: false, next_cursor: null } : data;
}

export async function getWorkHistory(userId) {
  if (!userId) return [];
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/work_history/${userId}`, { headers });
    return await parseJSONResponse(response);
  } catch (e) {
    console.error('작업 히스토리 조회 실패', e);
    return [];
  }
}
// 작업 히스토리 삭제
export async function deleteHistory(userId, processIds) {
  if (!userId || !processIds || processIds.length === 0) return null;
  try {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE_URL}/api/delete_history`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userId, process_ids: processIds })
    });
    return await parseJSONResponse(response);
  } catch (e) {
    console.error('작업 히스토리 삭제 실패', e);
    throw e;
  }
}
// 문의 메일 전송
export async function sendContactEmail(subject, message, file = null) {
  const session = get(sessionStore);
  const formData = new FormData();
  formData.append('user_email', session.email || '');
  formData.append('user_name', session.name || '');
  formData.append('subject', subject || '제목 없음');
  formData.append('message', message);
  if (file) {
    formData.append('image', file);
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/send_contact_email`, {
      method: 'POST',
      body: formData
    });
    return await parseJSONResponse(response);
  } catch (error) {
    console.error('문의 전송 오류:', error);
    throw error;
  }
}

// 결과 이미지 선택 (2장 중 1장 갤러리 저장)
export async function selectResult(processId, selectedUrl, userId) {
  try {
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const response = await fetch(`${API_BASE_URL}/api/select_result`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ process_id: processId, selected_url: selectedUrl, user_id: userId })
    });
    return await parseJSONResponse(response);
  } catch (e) {
    console.error('결과 선택 저장 실패', e);
    throw e;
  }
}

// 추천 스타일 목록 조회
export async function getRecommendedStyles() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/recommended_styles`);
    return await parseJSONResponse(response);
  } catch (error) {
    console.error('추천 스타일 조회 오류:', error);
    return [];
  }
}
